import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormProvider } from "react-hook-form";
import { Box, Button, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import { selectAuthError, selectIsBlockedUser, selectLoginAttempts } from "../redux";
import { useLoginFormManagement } from "../hooks";
import { PASSWORD_MAX_LENGTH } from "../utils/passwordPolicy";
import {
  LoginFormErrorAlert,
  FormTextField,
  FieldsValidationErrorAlert,
  BackdropLoading,
  TypeRadio,
  FormRadio,
} from "../components";
import { MessageModal, MessageModalTimer } from "../modals";

const keyPrefix = "forms.LoginForm";

export const LoginForm: FC = (): JSX.Element => {
  const { t } = useTranslation("translation", { keyPrefix });
  const loginAttempts = useSelector(selectLoginAttempts);
  const [showErroMessage, setShowErroMessage] = useState<boolean>(false);
  const [showAuthErroMessage, setShowAuthErroMessage] = useState<boolean>(false);
  const {
    clearErrorMessages,
    fieldsValidationErr,
    genericErr,
    loading,
    methods,
    submit,
    waitingProperties,
  } = useLoginFormManagement();

  const authError = useSelector(selectAuthError);
  const isBlocked = useSelector(selectIsBlockedUser);
  const typeLoginValue = methods.watch("typeLogin");

  const optionsLogin: TypeRadio[] = [
    {
      label: t("typeLogin.1"),
      value: "1",
    },
    {
      label: t("typeLogin.2"),
      value: "2",
    },
  ];

  useEffect(() => {
    !!authError && setShowAuthErroMessage(true);
  }, [authError, loginAttempts]);

  /**
   * `genericErr` cubre lo que `authError` no puede: la propiedad de URL
   * ausente y la falla de transporte, que no dejan respuesta del servicio que
   * traducir. Sin este efecto el hook lo calculaba y nadie lo pintaba —el
   * hallazgo E otra vez, esta vez con la guarda puesta pero muda—.
   */
  useEffect(() => {
    !!genericErr && setShowErroMessage(true);
  }, [genericErr]);

  return (
    <>
      {showAuthErroMessage && (
        <MessageModalTimer
          message={authError!}
          okLabel={t("modals.genericErr.ok")}
          timer={(loginAttempts !== null && loginAttempts <= 1) || isBlocked}
          onClickOk={() => {
            setShowAuthErroMessage(false);
          }}
          onClose={() => {
            setShowAuthErroMessage(false);
          }}
        />
      )}
      {showErroMessage && (
        <MessageModal
          message={genericErr!}
          okLabel={t("modals.genericErr.ok")}
          onClickOk={() => {
            setShowErroMessage(false);
            clearErrorMessages();
          }}
          onClose={() => {
            setShowErroMessage(false);
            clearErrorMessages();
          }}
        />
      )}
      {loading && <BackdropLoading />}
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(submit)}>
          <Stack spacing={2} pt={2}>
            {fieldsValidationErr && (
              <FieldsValidationErrorAlert error={fieldsValidationErr} />
            )}
            {((loginAttempts !== null && loginAttempts >= 0) && !!authError) && (
              <LoginFormErrorAlert />
            )}
            <FormTextField
              name="username"
              label={t(`user.${typeLoginValue}`)}
              inputProps={{ maxLength: 50 }}
              disabledErrors={true}
            />
            <FormTextField
              name="password"
              label={t("pass.label")}
              inputProps={{ maxLength: PASSWORD_MAX_LENGTH }}
              withoutSpaces
              type="password"
              disabledErrors
            />
            <FormRadio name="typeLogin" items={optionsLogin} />
            <Box pt={2}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={waitingProperties}
              >
                {t("submit.label")}
              </Button>
            </Box>
          </Stack>
        </form>
      </FormProvider>
    </>
  );
};
